import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';

import BubbleSpinner from 'legacy-learnosity-player/src/utility/BubbleSpinner';

import { isEmpty, each } from 'lodash';

import LearnosityProvider from 'legacy/src/utility/LearnosityProvider.js';
import InlineConfirmation from 'legacy-learnosity-player/src/utility/InlineConfirmation';
import ScreenModal from 'chameleon/ui-stack/ui/modals/screen-modal';

import DropdownMenu from './DropdownMenu';
import Filters from './Filters';

import '../utility/proctor.scss';
import '../../utility/bubble.scss';

class Proctoring extends PureComponent {
  static displayName = 'Proctoring';

  static propTypes = {
    learnosityServerData: PropTypes.object.isRequired,
    containerElement: PropTypes.string,
    apiType: PropTypes.string,
    onExtendTime: PropTypes.func,
    onPaused: PropTypes.func,
    onResume: PropTypes.func,
    router: PropTypes.object.isRequired,
  };

  static defaultProps = {
    learnosityServerData: null,
    readyListenerCallback: () => {},
    containerElement: 'learnosity_report',
    apiType: 'reports',
    onExtendTime: null,
    onPaused: () => {},
    onResume: () => {},
  };

  constructor(props) {
    super(props);

    this.state = {
      filterBy: null,
      dropdownDisabled: false,
      isLearnosityDataReady: false,
      modalValues: {
        minutes: 0,
        hours: 0,
        itemNumber: 1,
      },
    };

    this.report = {};

    this.readyListener = this.readyListener.bind(this);
    this.handleFilter = this.handleFilter.bind(this);
  }

  /**
   * Callback handler called when report has initialized
   * @param {object} report The main report handler
   * */
  readyListener(report) {
    this.report = report.getReport(this.props.containerElement);

    this.setState({
      isLearnosityDataReady: true,
    });
  }

  getSelectedUsernames = () => {
    const u = this.report.getSelectedUsers();
    let unames = [];
    each(u, (id) => {
      let checkbox = document.querySelector(
        `tr[data-id='${id}'] .lrn-td-checkbox input[type='checkbox']`,
      );
      if (checkbox) {
        // Fixing a learnosity bug with the selectall functionality. No checkbox - no selection
        let val = document.querySelector(
          `tr[data-id='${id}'] .lrn-td-user-name`,
        );
        unames.push(val.innerText);
      }
    });

    return unames || [];
  };

  noUsersSelected = () => this.getSelectedUsernames().length < 1;

  getExtendMinutes = () => {
    const {
      modalValues: { minutes, hours },
    } = this.state;
    const hr = hours ? parseInt(hours, 10) : 0;
    const min = minutes ? parseInt(minutes, 10) : 0;
    return hr * 60 + min;
  };

  renderModalContent = (modal) => {
    const commonProps = {
      handleClose: this.handleCloseModal,
      users: this.getSelectedUsernames(),
    };

    const r = this.report;
    const otherProps = {
      pause: {
        header: 'Confirm Action',
        footer:
          'This action saves answers for the following student(s), but won’t let the student(s) continue until you resume the test. Are you sure you want to do this?',
        handleSubmit: () => {
          const { onPause } = this.props;
          onPause(r.getSelectedUsers());
          r.pause(r.getSelectedUsers());
          this.handleCloseModal();
        },
      },
      resume: {
        header: 'Confirm Action',
        footer:
          'This action lets the following student(s) continue the test. Are you sure you want to do this?',
        handleSubmit: () => {
          const { onResume } = this.props;
          onResume(r.getSelectedUsers());
          r.unpause(r.getSelectedUsers());
          this.handleCloseModal();
        },
      },
      extendTime: {
        header: 'Confirm Action',
        footer: 'Extend Assessment Time',
        handleSubmit: () => {
          const { onExtendTime } = this.props;
          onExtendTime(r.getSelectedUsers(), this.getExtendMinutes());
          this.handleCloseModal();
        },
        renderControlledPortion: this.renderExtendTimeInputs,
      },
      goto: {
        header: 'Confirm Action',
        footer: "The selected students' assessment will navigate to this item.",
        handleSubmit: () => {
          const {
            modalValues: { itemNumber },
          } = this.state;
          r.goto(r.getSelectedUsers(), {
            item_number: parseInt(itemNumber, 10),
          });
          this.handleCloseModal();
        },
        renderControlledPortion: this.renderGotoInputs,
      },
      saveAndExit: {
        header: 'Confirm Action',
        footer:
          'This action saves answers for the following student(s) and closes the test. Are you sure you want to do this?',
        handleSubmit: () => {
          r.exitAndSave(r.getSelectedUsers());
          this.handleCloseModal();
        },
      },
      discardAndExit: {
        header: 'Confirm Action',
        footer:
          'This action discards answers for the following student(s) and exits the test. Are you sure you want to do this?',
        handleSubmit: () => {
          r.exitAndDiscard(r.getSelectedUsers());
          this.handleCloseModal();
        },
      },
      submitAndExit: {
        header: 'Confirm Action',
        footer:
          'This action will cause the following student(s) to submit their assessments and then exits the test. Are you sure you want to do this?',
        handleSubmit: () => {
          r.exitAndSubmit(r.getSelectedUsers());
          this.handleCloseModal();
        },
      },
    };

    const modals = {
      pause: () => (
        <InlineConfirmation {...commonProps} {...otherProps[modal]} />
      ),
      resume: () => (
        <InlineConfirmation {...commonProps} {...otherProps[modal]} />
      ),
      goto: () => (
        <InlineConfirmation {...commonProps} {...otherProps[modal]} />
      ),
      extendTime: () => (
        <InlineConfirmation
          {...commonProps}
          {...otherProps[modal]}
          yesLabel="Extend"
          noLabel="Cancel"
        />
      ),
      saveAndExit: () => (
        <InlineConfirmation {...commonProps} {...otherProps[modal]} />
      ),
      discardAndExit: () => (
        <InlineConfirmation {...commonProps} {...otherProps[modal]} />
      ),
      submitAndExit: () => (
        <InlineConfirmation {...commonProps} {...otherProps[modal]} />
      ),
    };

    const CurrentModal = () => modals[modal]();
    return <CurrentModal /> || null;
  };

  /**
   * Render extend time inputs for modal
   */
  renderExtendTimeInputs = () => {
    const {
      modalValues: { hours, minutes },
    } = this.state;

    return (
      <div className="Confirmation--body-extendTime">
        <p>
          Any values entered below will be added to the assessment time for each
          user selected.
        </p>
        <div>
          <input
            id="extend-time-hours"
            type="number"
            defaultValue={hours}
            onChange={this.handleChange('hours')}
            min="0"
            max="24"
          />
          <label htmlFor="extend-time-hours">hs</label>
          <input
            id="extend-time-minutes"
            type="number"
            defaultValue={minutes}
            onChange={this.handleChange('minutes')}
            min="0"
            max="59"
          />
          <label htmlFor="extend-time-minutes">min</label>
        </div>
      </div>
    );
  };

  /**
   * Render go to question inputs for modal
   */
  renderGotoInputs = () => (
    <div className="Confirmation--body-goto">
      Go to question #:&nbsp;
      <input
        type="text"
        defaultValue={1}
        onChange={this.handleChange('itemNumber')}
        disabled={this.noUsersSelected()}
      />
    </div>
  );

  /**
   * Open modal handler
   * */
  handleOpenModal = (modalType) => {
    const content = this.renderModalContent(modalType);

    if (content) {
      const proctorContainer = this.getProctorContainer();
      ReactDOM.render(
        <div className="inline-modal">{content}</div>,
        proctorContainer,
      );
      this.setState({ dropdownDisabled: true });
    }
  };

  /**
   * Close modal handler
   * */
  handleCloseModal = () => {
    this.clearModalContainer();
    this.resetModalControlledValues();
  };

  /**
   * Clear modal container element
   * */
  clearModalContainer = () => {
    const proctorContainer = this.getProctorContainer();
    ReactDOM.unmountComponentAtNode(proctorContainer);
    this.setState({ dropdownDisabled: false });
  };

  /**
   * Clear modal controlled values
   */
  resetModalControlledValues = () => {
    this.setState({ modalValues: { hours: 0, minutes: 0, itemNumber: 1 } });
  };

  /**
   * Get proctor container
   * Cache the selector
   * */
  getProctorContainer = () => {
    if (!this.modalContainer) {
      this.modalContainer = document.querySelector(
        `#proctor-wrapper #inline-proctoring-modal`,
      );
    }

    return this.modalContainer;
  };

  renderTitle = () => {
    const selector = `.learnosity-report.live-activitystatus-by-user .lrn-report-table-action h3`;
    const tileEl = document.querySelector(selector);
    return tileEl ? (
      tileEl.innerHTML
    ) : (
      <BubbleSpinner data-test-learnosity-loading />
    );
  };

  /**
   * Handle change inside of modal, record state here
   */
  handleChange = (field) => (e) => {
    const obj = {};
    obj[field] = e.target.value || 0;
    const { modalValues } = this.state;

    this.setState({ modalValues: { ...modalValues, ...obj } });
  };

  handleFilter(type) {
    this.setState({ filterBy: type });
  }

  onClose = () => {
    const { router } = this.props;
    const {
      params: { assignmentId, subject },
    } = router;
    router &&
      router.push({
        pathname: `/${subject}/assignments/progress/${assignmentId}`,
      });
  };

  render() {
    const { apiType, containerElement, learnosityServerData, children } =
      this.props;

    const { isLearnosityDataReady, filterBy, dropdownDisabled } = this.state;

    if (isEmpty(learnosityServerData)) {
      return (
        <div className="spinner-wrapper">
          <BubbleSpinner data-test-learnosity-loading />
        </div>
      );
    }

    const menuProps = {
      onShowModal: this.handleOpenModal,
    };

    return (
      <ScreenModal
        size="full"
        className="fullscreen opaque"
        title={<span className="capitalize">{this.renderTitle()}</span>}
        onClose={this.onClose}
      >
        <div
          id="proctor-wrapper"
          className={`${filterBy} h-screen overflow-y-scroll`}
        >
          <Filters handleFilter={this.handleFilter} />
          <div
            id="inline-proctoring-modal"
            className={dropdownDisabled ? 'hide-checkboxes' : ''}
          />
          <div id="proctoring-h3-title" />
          <DropdownMenu {...menuProps} dropdownDisabled={dropdownDisabled} />
          <div
            id={containerElement}
            className="Report"
            hidden={!isLearnosityDataReady}
            data-test-learnosity-report
          >
            <div className="report_content">
              <LearnosityProvider
                apiType={apiType}
                containerElement={containerElement}
                initOptions={learnosityServerData}
                readyListener={this.readyListener}
              >
                {children}
              </LearnosityProvider>
            </div>
          </div>
          {!isLearnosityDataReady && (
            <BubbleSpinner data-test-learnosity-internal-loading />
          )}
        </div>
      </ScreenModal>
    );
  }
}

export default Proctoring;
